import React, { useEffect, useId, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress, IconButton, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import Chart from 'react-apexcharts';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import { addDays, format, parseISO } from 'date-fns';
import * as SimpleBiReportsApi from './simpleBiReportsApi';

// Preserve Romanian wall time and offset supplied by the backend, including
// the repeated autumn hour, regardless of the browser's timezone.
const clock = (stamp) => stamp.slice(11, 16);
const interval = (row) => `${clock(row.start)}–${clock(row.end)}`;
const stampValid = (value) => typeof value === 'string' && Number.isFinite(Date.parse(value));
const intervalValid = (row) => row && stampValid(row.start) && stampValid(row.end);
const countValid = (value) => Number.isInteger(value) && value >= 0;

function validReport(data, date, location, bucketMinutes) {
  const summary = data?.summary;
  return data?.date === date && data.location === location && data.bucket_minutes === bucketMinutes
    && summary && countValid(summary.total_orders)
    && (summary.orders_per_hour === null || (Number.isFinite(summary.orders_per_hour) && summary.orders_per_hour >= 0))
    && (summary.peak_interval === null || (intervalValid(summary.peak_interval) && countValid(summary.peak_interval.orders)))
    && (summary.longest_gap === null || (intervalValid(summary.longest_gap) && (Number.isFinite(summary.longest_gap.minutes) && summary.longest_gap.minutes >= 0)))
    && Array.isArray(data.buckets) && data.buckets.every((row) => intervalValid(row) && countValid(row.orders))
    && Array.isArray(data.schedule?.periods) && data.schedule.periods.every(intervalValid)
    && (data.buckets.length === 0 ? data.display_range === null
      : intervalValid(data.display_range) && Number.isFinite(data.display_range.hours) && data.display_range.hours > 0);
}

export function formatGapDuration(minutes) {
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes) || minutes < 0) return '-';
  const rounded = Math.round(minutes);
  if (minutes > 0 && rounded === 0) return '<1 min';
  if (rounded < 60) return `${rounded} min`;
  return `${Math.floor(rounded / 60)}h ${rounded % 60}m`;
}

const scheduleHelp = 'Current weekly schedule defines the working hours used for the longest gap. Historical schedule changes and holidays are not available. The schedule does not filter orders or chart data.';
const averageHelp = 'Total orders divided by the elapsed hours in the chart range, from the start of the first order’s hour to the end of the last order’s hour. Includes quiet hours between orders; independent of schedule and bucket size.';
const gapHelp = 'Longest time without orders inside one scheduled period, including its opening and closing edges. Closed hours and orders outside the schedule do not count. An empty period counts in full; without a schedule the gap is unavailable. Today’s closing edges stop at the current time.';

function errorMessage(error) {
  if (error?.status === 401) return 'Your session expired. Sign in again.';
  if (error?.status === 403) return 'You do not have permission to view this location.';
  if (error?.status === 404) return 'This location is no longer available.';
  if (error?.status === 400) return 'The date, location or bucket size is invalid.';
  return error?.message === 'Invalid Order Flow response.'
    ? 'The report response is incomplete. Retry to load it again.'
    : 'Order Flow could not be loaded. Check your connection and retry.';
}

export default function OrderFlow({ client, date, onDateChange }) {
  const dateInput = useRef(null);
  const [dateError, setDateError] = useState(false);
  const [intervalsExpanded, setIntervalsExpanded] = useState(false);
  const intervalsId = useId();
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState('');
  const [bucketMinutes, setBucketMinutes] = useState(15);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const queryKey = `${date}/${location}/${bucketMinutes}/${retry}`;
  const todayParts = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const today = ['year', 'month', 'day'].map((part) => todayParts.find((value) => value.type === part).value).join('-');
  const dateNavigationButtonSx = {
    width: { xs: 40, md: 32 }, height: { xs: 40, md: 32 }, p: 0, flexShrink: 0,
    color: '#94a3b8', border: '1px solid transparent',
    '&:hover': { color: '#1a237e', bgcolor: 'rgba(26,35,126,0.05)', borderColor: 'rgba(26,35,126,0.10)' },
    '&.Mui-disabled': { color: 'rgba(148,163,184,0.35)' },
  };

  const navigateDate = (value) => {
    if (dateInput.current) dateInput.current.value = value;
    setDateError(false);
    if (value !== date) onDateChange(value);
  };

  useEffect(() => {
    // Keep the native input uncontrolled while editing its date segments. A
    // controlled valid-date value otherwise restores the old year mid-keystroke.
    if (dateInput.current && dateInput.current.value !== date) dateInput.current.value = date;
    setDateError(false);
  }, [date]);

  const selectDate = (input, showError = false) => {
    const valid = /^\d{4}-\d{2}-\d{2}$/.test(input.value) && input.validity.valid;
    setDateError(showError && !valid);
    if (valid && input.value !== date) onDateChange(input.value);
  };

  useEffect(() => {
    let active = true;
    setLocationsLoading(true);
    setLocationError('');
    const request = SimpleBiReportsApi.locationsListAsync(client);
    const timer = setTimeout(() => {
      if (active) {
        active = false;
        setLocationsLoading(false);
        setLocationError('Locations timed out. Retry to load them again.');
        request?.abort?.();
      }
    }, 30000);
    Promise.resolve(request).then((data) => {
      const rows = Array.isArray(data) ? data : data?.locations;
      if (!Array.isArray(rows) || !rows.every((row) => typeof row.iref === 'string' && typeof row.name === 'string')) {
        throw new Error('Invalid locations response.');
      }
      if (active) {
        setLocations(rows);
        setLocation((current) => rows.some((row) => row.iref === current) ? current : rows[0]?.iref || '');
      }
    }).catch((failure) => { if (active) setLocationError(errorMessage(failure)); })
      .finally(() => { clearTimeout(timer); if (active) setLocationsLoading(false); });
    return () => { active = false; clearTimeout(timer); request?.abort?.(); };
  }, [client, retry]);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError('');
    if (!location || !date || locationsLoading || locationError) return undefined;
    const request = SimpleBiReportsApi.ordersFlowAsync(client, { date, location, bucket_minutes: bucketMinutes });
    const timer = setTimeout(() => {
      if (active) { active = false; setError('Order Flow timed out. Retry to load it again.'); request?.abort?.(); }
    }, 30000);
    Promise.resolve(request).then((data) => {
      if (!validReport(data, date, location, bucketMinutes)) throw new Error('Invalid Order Flow response.');
      if (active) setResult({ key: queryKey, data });
    }).catch((failure) => { if (active) setError(errorMessage(failure)); })
      .finally(() => clearTimeout(timer));
    return () => { active = false; clearTimeout(timer); request?.abort?.(); };
  }, [client, date, location, bucketMinutes, queryKey, locationsLoading, locationError]);

  const data = result?.key === queryKey ? result.data : null;
  const peak = data?.summary.peak_interval;
  const gap = data?.summary.longest_gap;
  const repeatedHour = data?.buckets.some((row) => row.start.slice(-6) !== data.buckets[0].start.slice(-6));
  const label = (row) => repeatedHour
    ? `${clock(row.start)} (${row.start.slice(-6)})–${clock(row.end)} (${row.end.slice(-6)})`
    : interval(row);
  const loading = locationsLoading || (!!location && !data && !error && !locationError);

  return (
    <Box role="region" aria-label="Order Flow report" tabIndex={0}
      sx={{ bgcolor: '#fff', borderRadius: 2,
        height: { xs: 'calc(100vh - 92px)', md: 'calc(100vh - 104px)' },
        '@supports (height: 100dvh)': { height: { xs: 'calc(100dvh - 92px)', md: 'calc(100dvh - 104px)' } },
        p: 1.25, flex: '1 1 auto', overflow: 'auto', minHeight: { xs: 0, md: 540 }, minWidth: 0, boxSizing: 'border-box',
        scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) 110px', xl: 'auto minmax(150px, 320px) 110px' }, gap: 1, mb: 1.25, '& .MuiInputBase-root': { fontSize: '0.78rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }}>
        <Box role="group" aria-label="Report date navigation" sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25, gridColumn: { xs: '1 / -1', xl: 1 }, minWidth: 0 }}>
          <Tooltip title="Previous day"><span><IconButton aria-label="Previous day" sx={dateNavigationButtonSx}
            disabled={date <= '2000-01-01'} onClick={() => navigateDate(format(addDays(parseISO(date), -1), 'yyyy-MM-dd'))}>
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton></span></Tooltip>
        <TextField label="Date" type="date" size="small" defaultValue={date} inputRef={dateInput} sx={{ minWidth: 0 }}
          error={dateError} helperText={dateError ? 'Enter a valid date (2000–2100).' : undefined}
          InputLabelProps={{ shrink: true }} inputProps={{ min: '2000-01-01', max: '2100-12-31' }}
          onChange={(event) => selectDate(event.target)} onBlur={(event) => selectDate(event.target, true)}
          onKeyDown={(event) => { if (event.key === 'Enter') selectDate(event.target, true); }} />
          <Tooltip title="Next day"><span><IconButton aria-label="Next day" sx={dateNavigationButtonSx}
            disabled={date >= today} onClick={() => navigateDate(format(addDays(parseISO(date), 1), 'yyyy-MM-dd'))}>
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton></span></Tooltip>
          <Tooltip title="Go to today"><IconButton aria-label="Go to today" sx={dateNavigationButtonSx} onClick={() => navigateDate(today)}>
            <TodayRoundedIcon sx={{ fontSize: { xs: 19, md: 16 } }} />
          </IconButton></Tooltip>
        </Box>
        <TextField label="Location" select size="small" value={location} sx={{ minWidth: 0, gridRow: { xs: 2, xl: 1 }, gridColumn: { xs: 1, xl: 2 } }}
          disabled={locationsLoading || !locations.length} onChange={(event) => setLocation(event.target.value)}>
          {!locations.length && <MenuItem value="">No locations</MenuItem>}
          {locations.map((row) => <MenuItem key={row.iref} value={row.iref}>{row.name}</MenuItem>)}
        </TextField>
        <TextField label="Bucket size" select size="small" value={bucketMinutes} sx={{ minWidth: 0, gridRow: { xs: 2, xl: 1 }, gridColumn: { xs: 2, xl: 3 } }}
          onChange={(event) => setBucketMinutes(Number(event.target.value))}>
          <MenuItem value={15}>15 min</MenuItem><MenuItem value={60}>1 hour</MenuItem>
        </TextField>
      </Box>
      {(error || locationError) && <Alert severity="error" action={<Button color="inherit" onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
        {locationError || error}
      </Alert>}
      {loading && <Box role="status" sx={{ py: 3 }}><CircularProgress size={20} /> Loading Order Flow…</Box>}
      {!locationsLoading && !locationError && !locations.length && <Alert severity="info">No locations are available for your account.</Alert>}
      {data && <>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 0.75, mb: 1 }}>
          {[
            ['Total orders', data.summary.total_orders, '', 'Only orders with status 1 or 2 are included. All channels are included. Counts and intervals use registration time in Europe/Bucharest. Delivery / pickup may occur on another day; sales and stock consumption use that date.'],
            ['Avg / hour', data.summary.orders_per_hour ?? '-', '', averageHelp],
            ['Peak', peak?.orders ?? '-', peak ? label(peak) : ''],
            ['Longest gap', formatGapDuration(gap?.minutes), gap ? label(gap) : '', gapHelp],
          ].map(([title, value, range, help]) => <Card variant="outlined" key={title}
            sx={{ px: 1, py: 0.75, minWidth: 0, borderRadius: '10px', borderColor: 'rgba(15,23,42,0.08)', boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: 20, gap: 0.25 }}>
              <Typography noWrap sx={{ color: '#64748b', fontSize: '0.68rem', fontWeight: 800 }}>{title}</Typography>
              {help && <Tooltip title={help}><IconButton size="small" aria-label={`About ${title}`} sx={{ p: 0.25 }}>
                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
              </IconButton></Tooltip>}
            </Box>
            <Typography noWrap sx={{ color: '#111827', fontWeight: 900, fontSize: { xs: '1.2rem', sm: '1.3rem' }, lineHeight: 1.25 }}>{value}</Typography>
            {range && <Typography noWrap title={range} sx={{ color: '#64748b', fontSize: '0.68rem', lineHeight: 1.3, mt: 0.25 }}>{range}</Typography>}
          </Card>)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.5, rowGap: 0.25, mb: 0.5 }}>
          <Typography sx={{ color: '#64748b', fontSize: '0.72rem' }}>
            Current schedule: {data.schedule.periods.map(interval).join(', ') || 'Not available'}
          </Typography>
          <Tooltip title={scheduleHelp}><IconButton size="small" aria-label="About current schedule" sx={{ p: 0.25 }}>
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
          </IconButton></Tooltip>
          {data.summary.orders_outside_schedule > 0 && <Chip size="small" variant="outlined"
            label={`${data.summary.orders_outside_schedule} ${data.summary.orders_outside_schedule === 1 ? 'order' : 'orders'} outside current schedule`}
            sx={{ height: 22, fontSize: '0.68rem', color: '#64748b', borderColor: 'rgba(15,23,42,0.12)' }} />}
        </Box>
        {data.buckets.length > 0 ? <>
          <Chart type="bar" height={220} series={[{ name: 'Orders', data: data.buckets.map((row) => row.orders) }]}
            options={{ chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: false }, fontFamily: 'inherit' },
              colors: ['#1a237e'], dataLabels: { enabled: false },
              xaxis: { categories: data.buckets.map((row) => row.start), tickPlacement: 'between',
                labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '10px' },
                  formatter: (value) => {
                    const stamp = String(value);
                    const hour = Number(stamp.slice(11, 13));
                    const stride = data.display_range.hours > 18 ? 3 : data.display_range.hours > 8 ? 2 : 1;
                    if (stamp.slice(14, 16) !== '00' || hour % stride !== 0) return '';
                    return `${clock(stamp)}${repeatedHour ? ` (${stamp.slice(-6)})` : ''}`;
                  } } },
              yaxis: { min: 0, forceNiceScale: true, decimalsInFloat: 0, title: { text: 'Orders' }, labels: { style: { fontSize: '10px' } } },
              tooltip: { x: { formatter: (_, { dataPointIndex }) => label(data.buckets[dataPointIndex]) },
                y: { formatter: (value) => `${value} orders` } } }} />
          <Accordion disableGutters elevation={0} expanded={intervalsExpanded}
            onChange={(_, expanded) => setIntervalsExpanded(expanded)} TransitionProps={{ unmountOnExit: true }}
            sx={{ width: '100%', maxWidth: 400, mx: 'auto', border: '1px solid rgba(15,23,42,0.08)', borderRadius: '10px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary id={`${intervalsId}-header`} aria-controls={`${intervalsId}-content`}
              expandIcon={<ExpandMoreRoundedIcon fontSize="small" />}
              sx={{ minHeight: 38, px: 1, '& .MuiAccordionSummary-content': { my: 0.75 } }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: '#64748b' }}>
                Intervals ({data.buckets.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
            <TableContainer sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}><Table size="small" aria-label="Order Flow intervals"
            sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.75rem' }, '& .MuiTableCell-head': { fontWeight: 800, bgcolor: '#f8fafc', color: '#64748b' } }}>
            <TableHead><TableRow><TableCell>Interval</TableCell><TableCell align="right">Orders</TableCell></TableRow></TableHead>
            <TableBody>{data.buckets.map((row) => <TableRow key={row.start}
              sx={{ '& .MuiTableCell-root': { color: row.orders ? '#111827' : '#94a3b8' } }}>
              <TableCell>{label(row)}</TableCell><TableCell align="right">{row.orders}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
            </AccordionDetails>
          </Accordion>
        </> : <Box role="status" sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>No orders for this location and day.</Typography>
        </Box>}
      </>}
    </Box>
  );
}
